/* eslint-disable react/display-name */
import React from 'react';
import styled from 'styled-components';
import { slate1, white } from '@engage/colors/base';
import { spaces } from '@engage/styles/spaces';
import { label14 } from '@engage/typography/font-styles';
import { track } from '@members/analytics/api';
import { TopNavCopy } from '@members/copy/copy.generated';
import { TopNav } from '@members/copy/models.generated/TopNav';
import { CircledPlay } from '@members/icons/CircledPlay';
import { Collections as CollectionsIcon } from '@members/icons/Collections';
import { Programs as ProgramsIcon } from '@members/icons/Programs';
import { COLLECTIONS_ROUTE } from '@members/pg-collections/urls';
import { trackViewedClassGroupLibrary } from '@members/pg-homescreen/analytics';
import { LIBRARY_CATEGORY_SELECTION_ROUTE } from '@members/pg-library/urls';
import { PROGRAMS_ROUTE } from '../../../pg-programs/urls';
import { MainLink, makeLinkActivatable } from '../../sharedLinkStyles';
import { ANALYTICS_SOURCE } from '../analytics';

type WorkoutsDropdownProps = {
  isMenuOpen: boolean;
};

export const WorkoutsDropdown = React.forwardRef<HTMLUListElement, WorkoutsDropdownProps>(
  (props, ref) => {
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (!props.isMenuOpen) {
        return;
      }

      const links = document.querySelectorAll('.dropdown-link');
      const current = document.activeElement;
      let nextIndex = Array.from(links).findIndex(link => link === current);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          nextIndex = nextIndex + 1 >= links.length ? 0 : nextIndex + 1;
          (links[nextIndex] as HTMLElement).focus();
          break;

        case 'ArrowUp':
          event.preventDefault();
          nextIndex = nextIndex - 1 < 0 ? links.length - 1 : nextIndex - 1;
          (links[nextIndex] as HTMLElement).focus();
          break;

        case ' ':
          event.preventDefault();
          if (current instanceof HTMLElement) {
            current.click();
          }
          break;

        case 'Tab':
          // If we're on the last item and tabbing forward, or first item and tabbing backward
          if (
            (!event.shiftKey && nextIndex === links.length - 1) ||
            (event.shiftKey && nextIndex === 0)
          ) {
            // Find and click the Workouts button to close the menu
            const workoutsButton = document.querySelector(
              '[aria-controls="workouts-menu"]',
            ) as HTMLElement;
            workoutsButton?.click();
          }
          break;
      }
    };

    return (
      <WorkoutsContainer
        id="workouts-menu"
        role="menu"
        aria-hidden={!props.isMenuOpen}
        isMenuOpen={props.isMenuOpen}
        ref={ref}
        onKeyDown={handleKeyDown}
      >
        <li role="none">
          <ClassesLink />
        </li>
        <li role="none">
          <ProgramsLink />
        </li>
        <li role="none">
          <CollectionsLink />
        </li>
      </WorkoutsContainer>
    );
  },
);

const ClassesLink = () => {
  return (
    <WorkoutsDropdownLink
      className="dropdown-link"
      href={{
        pathname: LIBRARY_CATEGORY_SELECTION_ROUTE,
        query: { source: ANALYTICS_SOURCE },
      }}
      alias={LIBRARY_CATEGORY_SELECTION_ROUTE}
      data-test-id="classesLink"
      role="menuitem"
    >
      <CircledPlay />
      <TopNavCopy id={TopNav.Classes} />
    </WorkoutsDropdownLink>
  );
};

const CollectionsLink = () => {
  const trackAnalyticsLink = () => track(trackViewedClassGroupLibrary('Top Nav'));

  return (
    <WorkoutsDropdownLink
      className="dropdown-link"
      href={{
        pathname: COLLECTIONS_ROUTE,
        query: { source: ANALYTICS_SOURCE },
      }}
      alias={COLLECTIONS_ROUTE}
      data-test-id="collectionsLink"
      onClick={trackAnalyticsLink}
      role="menuitem"
    >
      <CollectionsIcon />
      <TopNavCopy id={TopNav.Collections} />
    </WorkoutsDropdownLink>
  );
};

const ProgramsLink = () => (
  <WorkoutsDropdownLink
    className="dropdown-link"
    href={{
      pathname: PROGRAMS_ROUTE,
      query: { source: ANALYTICS_SOURCE },
    }}
    alias={PROGRAMS_ROUTE}
    data-test-id="programsLink"
    role="menuitem"
  >
    <ProgramsIcon />
    <TopNavCopy id={TopNav.Programs} />
  </WorkoutsDropdownLink>
);

const StyledLink = styled(MainLink)`
  ${label14}
  margin-bottom: 0;
  display: flex;
  align-items: center;
  padding: ${spaces.small}px;
  gap: ${spaces.small}px;
  justify-content: flex-start;
  color: ${slate1};
  fill: ${slate1};
  background: ${white};

  &:hover {
    color: ${slate1};
    fill: ${slate1};
  }

  &:focus {
    outline: 2px solid #00a7e1;
    outline-offset: -2px;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const WorkoutsDropdownLink = makeLinkActivatable(StyledLink);

const WorkoutsContainer = styled.ul<{ isMenuOpen: boolean }>`
  border-radius: 4px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  position: absolute;
  top: 48px;
  width: 166px;
  background: ${white};
  ${p => !p.isMenuOpen && `display: none;`}
`;
